/** @jsxImportSource @emotion/react */

import { Typography } from "@a_team/ui-components";
import analytics from "analytics";
import { COLLABORATOR_ROLE_CHANGED } from "analytics/events";
import { RoleSelector } from "components/Collaborators/RoleSelector";
import { Role, getLabelForRole } from "configs/role";
import useCanUpdateWorkspace from "hooks/access/useCanUpdateWorkspace";
import useUserRoleFromAccount from "hooks/access/useUserRoleFromAccount";
import { AccountMember } from "models/Account";
import { useUpdateMissionRole } from "queries/rbac/useUpdateMissionRole";
import { useUpdateWorkspaceRole } from "queries/rbac/useUpdateWorkspaceRole";
import { useEffect, useMemo, useState } from "react";
import { useRootStore } from "store";

export const RoleSelectorForCollaborator = ({
  collaborator,
}: {
  collaborator: AccountMember;
}) => {
  const userId = collaborator.user.id;
  const [role, setRole] = useState<Role>(Role.WorkspaceMember);
  const roleFromAccount = useUserRoleFromAccount({ userId });
  const { mutate: updateWorkspaceRole } = useUpdateWorkspaceRole();
  const { mutate: updateMissionRole } = useUpdateMissionRole();
  const canUpdateWorkspace = useCanUpdateWorkspace();
  const {
    uiStore: { setToast },
    userStore: { user },
    missionSpecStore: { missionSpec },
  } = useRootStore();

  const isMissionInvite = useMemo(() => !!missionSpec, [missionSpec]);

  const isRoleReadOnly = useMemo(() => {
    if (!role) {
      return false;
    }

    // If current user is admin, they assume Team Advisor role
    if (user?.isAdmin && role === Role.TeamAdvisor) {
      return true;
    }

    // on workspace level
    if (!isMissionInvite && !canUpdateWorkspace) {
      return true;
    }

    // on mission level
    if (
      isMissionInvite &&
      [Role.TeamAdvisor, Role.WorkspaceAdmin].includes(role)
    ) {
      return true;
    }

    return false;
  }, [role, isMissionInvite, canUpdateWorkspace]);

  const handleUpdateSuccess = () => {
    setToast({
      text: `${collaborator.user.fullName}'s role has been changed`,
      type: "success",
    });
  };

  useEffect(() => {
    if (roleFromAccount) {
      setRole(roleFromAccount);
    }
  }, [roleFromAccount]);

  if (isRoleReadOnly) {
    return (
      <Typography variant="textMedium">{getLabelForRole(role)}</Typography>
    );
  }

  return (
    <RoleSelector
      role={role}
      onChange={(role) => {
        setRole(role);
        isMissionInvite && missionSpec?.id
          ? updateMissionRole(
              { userId, role, missionSpecId: missionSpec.id },
              { onSuccess: handleUpdateSuccess }
            )
          : updateWorkspaceRole(
              { userId, role },
              { onSuccess: handleUpdateSuccess }
            );
        analytics.track(COLLABORATOR_ROLE_CHANGED, {
          userId,
          fullName: collaborator.user.fullName,
          role,
        });
      }}
      allowedRoles={
        isMissionInvite
          ? [Role.MissionAdmin, Role.MissionMember]
          : [Role.WorkspaceAdmin, Role.WorkspaceMember]
      }
    />
  );
};
